import React, { useEffect, useState } from "react";
import api from "../../services/api";
import {
  PageContainer, PageHeader, Card, CardHeader,
  Table, Thead, Th, Td, TRow,
  Button, FormGroup, Input, Badge,
} from "../ui";

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

interface Vendor {
  id: string;
  name: string;
  contact_email: string;
  contact_phone: string;
  order_email: string;
  payment_terms: string;
  is_active: boolean;
  schedule: Array<{
    day_of_week: number;
    entry_type: string;
    cutoff_time: string;
  }>;
}

export default function VendorList() {
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: "",
    contact_email: "",
    contact_phone: "",
    order_email: "",
    payment_terms: "",
    min_order_amount: "",
    notes: "",
  });

  useEffect(() => {
    loadVendors();
  }, []);

  const loadVendors = async () => {
    const response = await api.get("/vendors?all=true");
    setVendors(response.data);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const data = {
      ...form,
      min_order_amount: form.min_order_amount ? Number(form.min_order_amount) : null,
    };
    if (editingId) {
      await api.put(`/vendors/${editingId}`, data);
    } else {
      await api.post("/vendors", data);
    }
    setShowForm(false);
    setEditingId(null);
    setForm({ name: "", contact_email: "", contact_phone: "", order_email: "", payment_terms: "", min_order_amount: "", notes: "" });
    loadVendors();
  };

  const editVendor = (vendor: Vendor) => {
    setForm({
      name: vendor.name,
      contact_email: vendor.contact_email || "",
      contact_phone: vendor.contact_phone || "",
      order_email: vendor.order_email || "",
      payment_terms: vendor.payment_terms || "",
      min_order_amount: "",
      notes: "",
    });
    setEditingId(vendor.id);
    setShowForm(true);
  };

  return (
    <PageContainer>
      <PageHeader
        title="Vendors"
        subtitle={`${vendors.length} vendor${vendors.length !== 1 ? "s" : ""}`}
        action={
          <Button onClick={() => { setShowForm(true); setEditingId(null); }}>
            + Add Vendor
          </Button>
        }
      />

      {showForm && (
        <Card className="mb-6">
          <CardHeader title={editingId ? "Edit Vendor" : "New Vendor"} />
          <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4">
            <FormGroup label="Name">
              <Input value={form.name} onChange={(v) => setForm({ ...form, name: v })} required />
            </FormGroup>
            <FormGroup label="Order Email">
              <Input value={form.order_email} onChange={(v) => setForm({ ...form, order_email: v })} />
            </FormGroup>
            <FormGroup label="Contact Phone">
              <Input value={form.contact_phone} onChange={(v) => setForm({ ...form, contact_phone: v })} />
            </FormGroup>
            <FormGroup label="Payment Terms">
              <Input value={form.payment_terms} onChange={(v) => setForm({ ...form, payment_terms: v })} placeholder="Net 7" />
            </FormGroup>
            <div className="col-span-2 flex gap-3">
              <Button type="submit">Save</Button>
              <Button variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
            </div>
          </form>
        </Card>
      )}

      <Table>
        <Thead>
          <Th>Vendor</Th>
          <Th>Terms</Th>
          <Th>Order Email</Th>
          <Th>Schedule</Th>
          <Th align="right">Actions</Th>
        </Thead>
        <tbody>
          {vendors.map((vendor, idx) => (
            <TRow key={vendor.id} index={idx}>
              <Td bold>{vendor.name}</Td>
              <Td>{vendor.payment_terms || "-"}</Td>
              <Td>{vendor.order_email || "-"}</Td>
              <Td>
                {vendor.schedule?.map((s, i) => (
                  <Badge key={i} variant="default">
                    {DAY_NAMES[s.day_of_week]} {s.entry_type === "order_due" ? "order" : "delivery"}
                    {s.cutoff_time ? ` ${s.cutoff_time}` : ""}
                  </Badge>
                ))}
              </Td>
              <Td align="right">
                <Button variant="ghost" size="sm" onClick={() => editVendor(vendor)}>Edit</Button>
              </Td>
            </TRow>
          ))}
        </tbody>
      </Table>
    </PageContainer>
  );
}
